import { useEffect, useState } from 'react';
import { Button, List, Skeleton } from 'antd';
import { ArrowDownIcon } from 'icons';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppSelector } from 'store';

import { globalConst, ISubjectData, parseSubjectName, useSubjectsData } from 'modules/global';

import { Modal } from 'components';

const SubjectsDrawer = () => {
  const t = useTranslations();
  const { query, push } = useRouter();
  const { subjects, isLoading: subjectsLoading } = useSubjectsData();
  const [selectedSubject, setSelectedSubject] = useState<ISubjectData>();
  const [open, setOpen] = useState(false);
  const isLoading = useAppSelector(state => state.routeLoading.isRouteLoading);
  const savedSubject =
    subjects?.subject?.find(subject => parseSubjectName(subject.name).toLowerCase() === query.subject) || subjects?.subject?.[0];

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onSave = () => {
    if (selectedSubject?.name === t('barchasi')) {
      push(globalConst.pageNames.TOPIC_TEST);
    } else {
      push(`${globalConst.pageNames.TOPIC_TEST}/${parseSubjectName(selectedSubject?.name)}`);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      handleClose();
    }
  }, [isLoading]);

  useEffect(() => {
    if (savedSubject) {
      setSelectedSubject(savedSubject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSubject]);

  return (
    <>
      <div>
        <div className="flex max-w-40 items-center rounded-xl px-4 min-h-10 gap-1 border-gray-300 border sm:max-w-full bg-white cursor-pointer" onClick={handleOpen}>
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="relative h-5 w-5 shrink-0">
              <Image
                src={savedSubject?.image || '/images/subject-barchasi.webp'}
                alt={savedSubject?.name?.slice(0, 1) || 'Subject alt'}
                loading="lazy"
                fill
                title="Subject list"
              />
            </div>
            <p className="overflow-hidden truncate text-ellipsis whitespace-nowrap">
              {savedSubject?.name || t('barchasi')}
            </p>
          </div>
          <ArrowDownIcon />
        </div>

        <Modal
          open={open}
          height="70%"
          onClose={handleClose}
          footer={
            <Button
              loading={isLoading}
              onClick={onSave}
              className="!my-2 !w-full !rounded-lg !border-transparent !bg-green-500 !py-5 !text-white"
            >
              {t('profile.saqlash')}
            </Button>
          }
        >
          <p className="p-3 text-sm text-gray-400">{t('Mavzular')}</p>
          {subjectsLoading ? (
            <div className="mt-4 space-y-3">
              {Array.from({ length: 10 }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton.Button key={index} active className="!h-10 !w-full" />
              ))}
            </div>
          ) : (
            <div className="mt-2">
              <List
                loading={subjectsLoading}
                itemLayout="horizontal"
                dataSource={subjects?.subject}
                renderItem={item => {
                  const isActive = parseSubjectName(selectedSubject?.name || '') === parseSubjectName(item.name);

                  return (
                    <List.Item onClick={() => setSelectedSubject(item)} className="!py-1">
                      <List.Item.Meta
                        className="cursor-pointer !items-center rounded-md !py-2 pl-4 transition-all"
                        style={{
                          background: isActive ? `${item.color}1f` : 'transparent',
                          borderRight: `3px solid ${isActive ? item.color : 'transparent'}`
                        }}
                        avatar={
                          <Image
                            src={item.image}
                            alt={item.name.slice(0, 1)}
                            loading="lazy"
                            width={32}
                            height={32}
                            title="Subject list"
                          />
                        }
                        title={
                          <span
                            style={{
                              color: isActive ? item.color : 'black',
                              fontWeight: isActive ? '500' : 'normal'
                            }}
                          >
                            {item.name}
                          </span>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default SubjectsDrawer;
